import { type RefObject, useCallback, useLayoutEffect, useRef } from 'react'
import { withoutForbiddenXmlCharacters } from '@eformulieren/model'
import * as Y from 'yjs'
import { textChange } from '../../document/sectionDocument.js'
import { useSnapshot } from '../../document/snapshot.js'

/**
 * Een invoerveld of tekstvak gebonden aan een `Y.Text`, zodat twee mensen tegelijk in hetzelfde
 * tekstveld kunnen typen zonder dat er iets verdwijnt (R-72, ontwerp 6.8).
 *
 * Wat deze binding doet, en waarom elk stuk er staat:
 *
 * - **Alleen het verschil gaat naar het document.** Na elke invoer vergelijkt de binding de tekst
 *   in het element met de tekst van vóór die invoer, en past ze dat ene stuk toe (`textChange`).
 *   De hele tekst terugschrijven zou wissen wat een medeschrijver intussen typte.
 * - **Het element is niet gecontroleerd door React.** Een gecontroleerd `value` zet bij elke
 *   wijziging van buiten de cursor aan het eind; hier zet de binding de tekst zelf en schuift ze
 *   de cursor mee met wat er ervóór is ingevoegd of weggehaald (`mapPosition`).
 * - **Niet tijdens het samenstellen van een teken.** Een dode toets op een Mac (´ en dan e) of een
 *   invoermethode voor een ander schrift stelt een teken in stappen samen. De tekst van het
 *   element aanraken midden in zo'n stap breekt hem af. Wat er dan van een ander binnenkomt wacht
 *   tot het teken af is; de eigen invoer gaat pas daarna naar het document, op de plek waar ze
 *   hoort na wat er intussen binnenkwam.
 * - **Ongedaan maken komt van de samenwerklaag** (valkuil 5). Een `Y.UndoManager` op deze tekst,
 *   die alleen de eigen wijzigingen van deze binding volgt: ongedaan maken haalt nooit de tekst
 *   van een medeschrijver weg. De eigen historie van de browser wordt onderschept, want die kent
 *   de tekst van de ander niet en zou hem bij ongedaan maken overschrijven.
 *
 * De teller leest de tekst uit het document en niet uit het element, zodat het getal onder het veld
 * en de grens waarop indienen stopt over dezelfde samengevoegde tekst gaan (R-12, R-13).
 */
export function useTextBinding<E extends HTMLInputElement | HTMLTextAreaElement>(
  text: Y.Text,
  options: {
    readonly editable: boolean
    readonly onOwnChange?: (() => void) | undefined
    /** Na elke eigen invoer: of er een teken is weggelaten dat niet kan worden verstuurd. */
    readonly onFiltered?: ((removed: boolean) => void) | undefined
  },
): { readonly ref: RefObject<E | null>; readonly current: string } {
  const ref = useRef<E | null>(null)
  const editable = useRef(options.editable)
  editable.current = options.editable
  const onOwnChange = useRef(options.onOwnChange)
  onOwnChange.current = options.onOwnChange
  const onFiltered = useRef(options.onFiltered)
  onFiltered.current = options.onFiltered

  const subscribe = useCallback(
    (redraw: () => void) => {
      text.observe(redraw)
      return () => text.unobserve(redraw)
    },
    [text],
  )
  const read = useCallback(() => text.toString(), [text])
  const current = useSnapshot(subscribe, read)

  useLayoutEffect(() => {
    const element = ref.current
    if (element === null) return undefined
    return bind(element, text, {
      editable: () => editable.current,
      onOwnChange: () => onOwnChange.current?.(),
      onFiltered: (removed) => onFiltered.current?.(removed),
    })
  }, [text])

  return { ref, current }
}

type Delta = Y.YTextEvent['delta']

/**
 * De binding zelf, zonder React: zo is hij te testen met een element en twee documenten.
 *
 * Geeft de opzegging terug.
 */
export function bind(
  element: HTMLInputElement | HTMLTextAreaElement,
  text: Y.Text,
  options: {
    readonly editable: () => boolean
    readonly onOwnChange: () => void
    readonly onFiltered?: (removed: boolean) => void
  },
): () => void {
  /** De origin van de eigen wijzigingen; alleen die volgt het ongedaan maken. */
  const own = {}
  const undo = new Y.UndoManager(text, { trackedOrigins: new Set([own]) })

  element.value = text.toString()
  /** Wat het element toonde na de laatste keer dat het met het document gelijk liep. */
  let shadow = element.value
  let composing = false
  /** Wat er van een ander binnenkwam terwijl er een teken werd samengesteld. */
  let pending: Delta[] = []

  const flush = (): void => {
    const shown = element.value
    if (!options.editable()) {
      // Alleen-lezen: de browser houdt invoer al tegen (`readOnly`), dit is het tweede slot.
      if (shown !== shadow) element.value = shadow
      return
    }
    const hadPending = pending.length > 0
    /**
     * Waartegen de invoer wordt vergeleken: de tekst zelf, of wat het element toonde.
     *
     * Zonder wijziging van een ander onderweg is dat de tekst in het document, en niet wat het
     * element toont, want die twee kunnen verschillen zonder dat iemand iets deed: een invoerveld
     * van één regel haalt een regeleinde weg en een tekstvak maakt van CR LF een LF. Zo'n tekst
     * komt van het processysteem of uit een opmerking die de pod zelf schreef. Wie dan tegen het
     * element zou rekenen, zet elke toets een paar tekens te vroeg in het document, bij iedereen.
     * Tegen de tekst gerekend verdwijnt bij de eerste toets alleen het regeleinde dat het veld toch
     * niet kon tonen, zoals vroeger bij een waarde.
     *
     * Kwam er tijdens het samenstellen van een teken iets van een ander binnen, dan toont het
     * element de stand van daarvóór, en is die stand het enige juiste vergelijkingspunt; de
     * wijziging gaat daarna door de deltas van de ander heen naar haar plek in het document.
     */
    const base = hadPending ? shadow : text.toString()
    const change = textChange(base, shown, element.selectionEnd ?? undefined)
    if (change === undefined) {
      if (!hadPending) return
      // Een samenstelling die op niets uitliep: alleen de tekst van de ander tonen, met de cursor
      // daarachteraan meegeschoven, en niet aan het begin van het veld.
      let start = element.selectionStart ?? 0
      let end = element.selectionEnd ?? start
      for (const delta of pending) {
        start = mapPosition(delta, start)
        end = mapPosition(delta, end)
      }
      pending = []
      element.value = text.toString()
      shadow = element.value
      if (isFocused(element)) element.setSelectionRange(start, end)
      return
    }
    let index = change.index
    let end = index + change.deleted
    for (const delta of pending) {
      index = mapPosition(delta, index)
      end = mapPosition(delta, end)
    }
    pending = []
    // Een teken dat XML 1.0 niet toelaat gaat niet het document in (koppelvlak hoofdstuk 3): het komt
    // mee met plakken, en het bericht aan het processysteem kan het niet dragen. Wat overblijft gaat
    // gewoon door, en het element toont hieronder de tekst zoals hij in het document staat.
    const inserted = withoutForbiddenXmlCharacters(change.inserted)
    if (change.inserted !== '') options.onFiltered?.(inserted !== change.inserted)
    if (end === index && inserted === '') {
      element.value = text.toString()
      shadow = element.value
      if (isFocused(element)) element.setSelectionRange(index, index)
      return
    }
    const action = (): void => {
      if (end > index) text.delete(index, end - index)
      if (inserted !== '') text.insert(index, inserted)
      options.onOwnChange()
    }
    if (text.doc === null) action()
    else text.doc.transact(action, own)
    const merged = text.toString()
    if (hadPending || merged !== shown) {
      element.value = merged
      const caret = index + inserted.length
      if (isFocused(element)) element.setSelectionRange(caret, caret)
    }
    shadow = merged
  }

  const onRemote = (event: Y.YTextEvent, transaction: Y.Transaction): void => {
    if (transaction.origin === own) return
    if (composing) {
      pending.push(event.delta)
      return
    }
    const focused = isFocused(element)
    const start = element.selectionStart
    const end = element.selectionEnd
    const direction = element.selectionDirection ?? undefined
    element.value = text.toString()
    shadow = element.value
    if (focused && start !== null && end !== null) {
      element.setSelectionRange(mapPosition(event.delta, start), mapPosition(event.delta, end), direction)
    }
  }

  const onInput = (event: Event): void => {
    if (composing || (event as InputEvent).isComposing === true) return
    flush()
  }
  const onCompositionStart = (): void => {
    composing = true
  }
  const onCompositionEnd = (): void => {
    composing = false
    flush()
  }

  const undoOwn = (): void => {
    if (undo.undo() !== null) options.onOwnChange()
  }
  const redoOwn = (): void => {
    if (undo.redo() !== null) options.onOwnChange()
  }
  const onKeyDown = (raw: Event): void => {
    const event = raw as KeyboardEvent
    if (composing || !options.editable()) return
    const modifier = event.metaKey || event.ctrlKey
    if (!modifier || event.altKey) return
    const key = event.key.toLowerCase()
    if (key === 'z' && !event.shiftKey) {
      event.preventDefault()
      undoOwn()
    } else if ((key === 'z' && event.shiftKey) || (key === 'y' && event.ctrlKey && !event.metaKey)) {
      event.preventDefault()
      redoOwn()
    }
  }
  /** Ongedaan maken uit het menu van de browser, zonder toets. */
  const onBeforeInput = (event: Event): void => {
    const kind = (event as InputEvent).inputType
    if (kind !== 'historyUndo' && kind !== 'historyRedo') return
    event.preventDefault()
    if (composing || !options.editable()) return
    if (kind === 'historyUndo') undoOwn()
    else redoOwn()
  }

  text.observe(onRemote)
  element.addEventListener('input', onInput)
  element.addEventListener('compositionstart', onCompositionStart)
  element.addEventListener('compositionend', onCompositionEnd)
  element.addEventListener('keydown', onKeyDown)
  element.addEventListener('beforeinput', onBeforeInput)
  return () => {
    text.unobserve(onRemote)
    element.removeEventListener('input', onInput)
    element.removeEventListener('compositionstart', onCompositionStart)
    element.removeEventListener('compositionend', onCompositionEnd)
    element.removeEventListener('keydown', onKeyDown)
    element.removeEventListener('beforeinput', onBeforeInput)
    undo.destroy()
  }
}

function isFocused(element: Element): boolean {
  return element.ownerDocument.activeElement === element
}

/**
 * Waar een positie terechtkomt na een wijziging van een ander.
 *
 * Tekst die vóór de positie wordt ingevoegd schuift haar op, tekst die ervóór wordt weggehaald
 * schuift haar terug. Een invoeging precies op de positie laat haar staan: wie typt waar een ander
 * net zijn cursor heeft, duwt die cursor niet weg.
 */
function mapPosition(delta: Delta, position: number): number {
  let index = 0
  let result = position
  for (const op of delta) {
    if (index > position) break
    if (op.retain !== undefined) {
      index += op.retain
    } else if (op.insert !== undefined) {
      if (index < position) result += typeof op.insert === 'string' ? op.insert.length : 1
    } else if (op.delete !== undefined) {
      if (index < position) result -= Math.min(op.delete, position - index)
      index += op.delete
    }
  }
  return result
}
